import { apiFetch, publicApiFetch } from '../httpClient';

const KAKAO_LOGIN_ENDPOINT = '/api/auth/kakao/login';
const APPLE_LOGIN_ENDPOINT =
  process.env.REACT_APP_APPLE_LOGIN_ENDPOINT || '/api/auth/apple/login';
const EMAIL_LOGIN_ENDPOINT =
  process.env.REACT_APP_EMAIL_LOGIN_ENDPOINT || '/api/auth/email/login';
const EMAIL_SIGNUP_ENDPOINT =
  process.env.REACT_APP_EMAIL_SIGNUP_ENDPOINT || '/api/auth/email/signup';
const EMAIL_SEND_CODE_ENDPOINT =
  process.env.REACT_APP_EMAIL_SEND_CODE_ENDPOINT || '/api/auth/email/send-code';
const EMAIL_VERIFY_CODE_ENDPOINT =
  process.env.REACT_APP_EMAIL_VERIFY_CODE_ENDPOINT || '/api/auth/email/verify-code';
const EMAIL_RESET_PASSWORD_ENDPOINT =
  process.env.REACT_APP_EMAIL_RESET_PASSWORD_ENDPOINT || '/api/auth/email/reset-password';
const REFRESH_ENDPOINT = '/api/auth/refresh';
const LOGOUT_ENDPOINT = '/api/auth/logout';
const ME_ENDPOINT = '/api/auth/me';
const UPDATE_ME_ENDPOINT =
  process.env.REACT_APP_USER_UPDATE_ENDPOINT || '/api/users/me';
const DEACTIVATE_ME_ENDPOINT =
  process.env.REACT_APP_USER_DEACTIVATE_ENDPOINT || '/api/users/me';

// 카카오 인가 코드를 우리 백엔드 인증 토큰으로 교환한다.
export async function loginWithKakaoCode({
  code,
  agreedTerms = false,
  agreedPrivacy = false,
  agreedMarketing = false,
}) {
  if (!code) {
    throw new Error('카카오 인가 코드(code)가 필요합니다.');
  }

  return publicApiFetch(KAKAO_LOGIN_ENDPOINT, {
    method: 'POST',
    body: {
      code,
      agreedTerms,
      agreedPrivacy,
      agreedMarketing,
    },
  });
}

// Apple identityToken을 우리 백엔드 인증 토큰으로 교환한다.
export async function loginWithAppleIdentity({
  identityToken,
  nickname = null,
  email = null,
  agreedTerms = false,
  agreedPrivacy = false,
  agreedMarketing = false,
}) {
  const normalizedIdentityToken = String(identityToken || '').trim();
  if (!normalizedIdentityToken) {
    throw new Error('Apple identityToken이 필요합니다.');
  }

  return publicApiFetch(APPLE_LOGIN_ENDPOINT, {
    method: 'POST',
    body: {
      identityToken: normalizedIdentityToken,
      nickname,
      email,
      agreedTerms,
      agreedPrivacy,
      agreedMarketing,
    },
  });
}

// 이메일/비밀번호 기반 로그인으로 백엔드 인증 토큰을 발급받는다.
export async function loginWithEmail({ email, password }) {
  const normalizedEmail = String(email || '').trim();
  if (!normalizedEmail || !password) {
    throw new Error('이메일과 비밀번호를 입력해 주세요.');
  }

  return publicApiFetch(EMAIL_LOGIN_ENDPOINT, {
    method: 'POST',
    body: {
      email: normalizedEmail,
      password,
    },
  });
}

// 이메일 인증코드 발송을 요청한다.
export async function sendEmailVerificationCode({ email }) {
  const normalizedEmail = String(email || '').trim();
  if (!normalizedEmail) {
    throw new Error('이메일을 입력해 주세요.');
  }

  return publicApiFetch(EMAIL_SEND_CODE_ENDPOINT, {
    method: 'POST',
    body: {
      email: normalizedEmail,
    },
  });
}

// 이메일 인증코드를 검증한다.
export async function verifyEmailCode({ email, code }) {
  const normalizedEmail = String(email || '').trim();
  const normalizedCode = String(code || '').trim();
  if (!normalizedEmail || !normalizedCode) {
    throw new Error('이메일과 인증코드를 입력해 주세요.');
  }

  return publicApiFetch(EMAIL_VERIFY_CODE_ENDPOINT, {
    method: 'POST',
    body: {
      email: normalizedEmail,
      code: normalizedCode,
    },
  });
}

// 이메일 인증코드를 사용해 비밀번호를 재설정한다.
export async function resetEmailPassword({ email, code, newPassword }) {
  const normalizedEmail = String(email || '').trim();
  const normalizedCode = String(code || '').trim();
  const normalizedNewPassword = String(newPassword || '');

  if (!normalizedEmail || !normalizedCode || !normalizedNewPassword) {
    throw new Error('이메일, 인증코드, 새 비밀번호를 입력해 주세요.');
  }

  return publicApiFetch(EMAIL_RESET_PASSWORD_ENDPOINT, {
    method: 'POST',
    body: {
      email: normalizedEmail,
      code: normalizedCode,
      newPassword: normalizedNewPassword,
    },
  });
}

// 이메일 회원가입 요청을 보낸다. 백엔드 정책에 따라 토큰이 함께 반환될 수 있다.
export async function signupWithEmail({
  email,
  password,
  nickname,
  agreedTerms = false,
  agreedPrivacy = false,
  agreedMarketing = false,
}) {
  const normalizedEmail = String(email || '').trim();
  const normalizedNickname = String(nickname || '').trim();

  if (!normalizedEmail || !password || !normalizedNickname) {
    throw new Error('이메일, 비밀번호, 닉네임을 입력해 주세요.');
  }

  return publicApiFetch(EMAIL_SIGNUP_ENDPOINT, {
    method: 'POST',
    body: {
      email: normalizedEmail,
      password,
      nickname: normalizedNickname,
      agreedTerms,
      agreedPrivacy,
      agreedMarketing,
    },
  });
}

// 저장된 refresh token으로 access/refresh 토큰 페어를 재발급한다.
export async function refreshAuthToken(refreshToken) {
  if (!refreshToken) {
    throw new Error('리프레시 토큰이 필요합니다.');
  }

  return publicApiFetch(REFRESH_ENDPOINT, {
    method: 'POST',
    body: { refreshToken },
  });
}

// 백엔드 세션/토큰 무효화를 위한 로그아웃 API 호출.
export async function logoutWithRefreshToken(refreshToken) {
  if (!refreshToken) {
    return null;
  }

  return publicApiFetch(LOGOUT_ENDPOINT, {
    method: 'POST',
    body: { refreshToken },
  });
}

// 현재 로그인 사용자 정보를 조회해 앱 인증 상태를 복원한다.
export async function getMyProfile() {
  return apiFetch(ME_ENDPOINT, {
    method: 'GET',
  });
}

// 내 닉네임/프로필 이미지를 수정한다.
export async function updateMyProfile(profilePayload) {
  return apiFetch(UPDATE_ME_ENDPOINT, {
    method: 'PATCH',
    body: profilePayload,
  });
}

// 내 계정을 비활성화(회원탈퇴)한다.
export async function deactivateMyProfile() {
  return apiFetch(DEACTIVATE_ME_ENDPOINT, {
    method: 'DELETE',
  });
}
